import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2,
  Save,
  Edit2,
  X,
  User,
  Mic,
  Stethoscope,
  ShoppingCart,
  Podcast,
  Plus,
  ImageIcon
} from 'lucide-react';

// Voice preset options for easy selection
const VOICE_PRESETS = [
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah', gender: 'female', accent: 'US' },
  { id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice', gender: 'female', accent: 'UK' },
  { id: 'FGY2WhTYpPnrIDTdsKH5', name: 'Laura', gender: 'female', accent: 'AU' },
  { id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily', gender: 'female', accent: 'UK' },
  { id: 'XrExE9yKIg1WjnnlVkGX', name: 'Matilda', gender: 'female', accent: 'AU' },
  { id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica', gender: 'female', accent: 'US' },
  { id: 'iP95p4xoKVk53GoZ742B', name: 'Chris', gender: 'male', accent: 'US' },
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George', gender: 'male', accent: 'UK' },
  { id: 'N2lVS1w4EtoT3dr4eOWO', name: 'Callum', gender: 'male', accent: 'AU' },
  { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam', gender: 'male', accent: 'US' },
  { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniel', gender: 'male', accent: 'UK' },
  { id: 'cjVigY5qzO86Huf0OWal', name: 'Eric', gender: 'male', accent: 'US' },
];

interface VoiceAgent {
  id: string;
  internal_id: string;
  name: string;
  elevenlabs_agent_id: string;
  elevenlabs_voice_id: string | null;
  voice_style: string | null;
  gender: 'male' | 'female';
  accent: 'AU' | 'US' | 'UK';
  agent_type: 'patient' | 'customer' | 'examiner';
  description: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface NewAgentForm {
  name: string;
  elevenlabs_agent_id: string;
  elevenlabs_voice_id: string;
  gender: 'male' | 'female';
  accent: 'AU' | 'US' | 'UK';
  agent_type: 'patient' | 'customer' | 'examiner' | 'podcast';
  description: string;
}

const defaultNewAgent: NewAgentForm = {
  name: '',
  elevenlabs_agent_id: '',
  elevenlabs_voice_id: '',
  gender: 'female',
  accent: 'AU',
  agent_type: 'patient',
  description: ''
};

// Helper to get matching presets for a gender/accent
function getMatchingPresets(gender: string, accent: string) {
  return VOICE_PRESETS.filter(p => p.gender === gender && p.accent === accent);
}

export function VoiceAgentsManager() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editAgentId, setEditAgentId] = useState('');
  const [editVoiceId, setEditVoiceId] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newAgent, setNewAgent] = useState<NewAgentForm>(defaultNewAgent);

  // Fetch voice agents
  const { data: voiceAgents, isLoading } = useQuery({
    queryKey: ['voice-agents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('voice_agents')
        .select('*')
        .order('agent_type', { ascending: true })
        .order('gender', { ascending: true })
        .order('accent', { ascending: true });

      if (error) throw error;
      return data as VoiceAgent[];
    }
  });

  // Update voice agent mutation
  const updateAgentMutation = useMutation({
    mutationFn: async ({ id, name, elevenlabs_agent_id, elevenlabs_voice_id, description, avatar_url }: { 
      id: string; 
      name: string; 
      elevenlabs_agent_id: string;
      elevenlabs_voice_id: string;
      description: string;
      avatar_url: string;
    }) => {
      const { error } = await supabase
        .from('voice_agents')
        .update({ 
          name, 
          elevenlabs_agent_id,
          elevenlabs_voice_id: elevenlabs_voice_id || null,
          description: description || null,
          avatar_url: avatar_url || null,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-agents'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-voices'] });
      setEditingId(null);
      setEditName('');
      setEditAgentId('');
      setEditVoiceId('');
      setEditDescription('');
      setEditAvatarUrl('');
      toast.success('Voice agent updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update voice agent: ' + error.message);
    }
  });

  // Create voice agent mutation
  const createAgentMutation = useMutation({
    mutationFn: async (agent: NewAgentForm) => {
      const internal_id = `${agent.agent_type}-${agent.gender}-${agent.accent}-${Date.now()}`.toLowerCase();
      const { error } = await supabase
        .from('voice_agents')
        .insert({
          internal_id,
          name: agent.name,
          elevenlabs_agent_id: agent.elevenlabs_agent_id,
          gender: agent.gender,
          accent: agent.accent,
          agent_type: agent.agent_type,
          description: agent.description || null,
          is_active: true
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-agents'] });
      setIsAddDialogOpen(false);
      setNewAgent(defaultNewAgent);
      toast.success('Voice agent added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add voice agent: ' + error.message);
    }
  });

  const startEdit = (agent: VoiceAgent) => {
    setEditingId(agent.id);
    setEditName(agent.name);
    setEditAgentId(agent.elevenlabs_agent_id);
    setEditVoiceId(agent.elevenlabs_voice_id || '');
    setEditDescription(agent.description || '');
    setEditAvatarUrl(agent.avatar_url || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditAgentId('');
    setEditVoiceId('');
    setEditDescription('');
    setEditAvatarUrl('');
  };

  const saveEdit = () => {
    if (editingId && editName.trim() && editAgentId.trim()) {
      updateAgentMutation.mutate({ 
        id: editingId, 
        name: editName.trim(),
        elevenlabs_agent_id: editAgentId.trim(),
        elevenlabs_voice_id: editVoiceId.trim(),
        description: editDescription.trim(),
        avatar_url: editAvatarUrl.trim(),
      });
    }
  };

  const handleCreateAgent = () => {
    if (!newAgent.name.trim() || !newAgent.elevenlabs_agent_id.trim()) return;
    createAgentMutation.mutate(newAgent);
  };

  const getGenderBadge = (gender: string) => {
    return (
      <Badge variant="outline" className={gender === 'female' ? 'border-pink-300 text-pink-700' : 'border-blue-300 text-blue-700'}>
        {gender}
      </Badge>
    );
  };

  const getAccentBadge = (accent: string) => {
    const accents: Record<string, { label: string; className: string }> = {
      'AU': { label: 'Australian', className: 'bg-yellow-100 text-yellow-800' },
      'US': { label: 'American', className: 'bg-blue-100 text-blue-800' },
      'UK': { label: 'British', className: 'bg-red-100 text-red-800' }
    };
    const config = accents[accent] || { label: accent, className: '' };
    return <Badge variant="secondary" className={config.className}>{config.label}</Badge>;
  };

  const patientAgents = voiceAgents?.filter(a => a.agent_type === 'patient') || [];
  const customerAgents = voiceAgents?.filter(a => a.agent_type === 'customer') || [];
  const examinerAgents = voiceAgents?.filter(a => a.agent_type === 'examiner') || [];
  const podcastAgents = voiceAgents?.filter(a => a.agent_type === 'podcast') || [];

  const renderAgentTable = (agents: VoiceAgent[]) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">Avatar</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Gender</TableHead>
          <TableHead>Accent</TableHead>
          <TableHead>Agent ID (Simulations)</TableHead>
          <TableHead>TTS Voice (Podcasts)</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {agents.map((agent) => {
          const matchingPresets = getMatchingPresets(agent.gender, agent.accent);
          const currentPreset = VOICE_PRESETS.find(p => p.id === agent.elevenlabs_voice_id);
          
          return (
            <TableRow key={agent.id}>
              {/* Avatar preview */}
              <TableCell>
                {editingId === agent.id ? (
                  <div className="flex flex-col items-center gap-1">
                    {editAvatarUrl ? (
                      <img src={editAvatarUrl} alt="" className="w-10 h-10 rounded-full object-cover border" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                    <Input
                      value={editAvatarUrl}
                      onChange={(e) => setEditAvatarUrl(e.target.value)}
                      className="h-7 text-xs w-32"
                      placeholder="Avatar URL..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                  </div>
                ) : agent.avatar_url ? (
                  <img src={agent.avatar_url} alt={agent.name} className="w-10 h-10 rounded-full object-cover border" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </TableCell>
              <TableCell>
                {editingId === agent.id ? (
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8 w-40"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="font-medium">{agent.name}</span>
                )}
              </TableCell>
              <TableCell className="max-w-[150px]">
                {editingId === agent.id ? (
                  <Input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="h-8"
                    placeholder="e.g., Mid-30s, calm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground truncate block">
                    {agent.description || <span className="italic text-muted-foreground/50">—</span>}
                  </span>
                )}
              </TableCell>
              <TableCell>{getGenderBadge(agent.gender)}</TableCell>
              <TableCell>{getAccentBadge(agent.accent)}</TableCell>
              <TableCell>
                {editingId === agent.id ? (
                  <Input
                    value={editAgentId}
                    onChange={(e) => setEditAgentId(e.target.value)}
                    className="h-8 font-mono text-xs w-36"
                    placeholder="agent_..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                ) : (
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    {agent.elevenlabs_agent_id.length > 16 
                      ? `${agent.elevenlabs_agent_id.substring(0, 16)}...`
                      : agent.elevenlabs_agent_id}
                  </code>
                )}
              </TableCell>
              <TableCell>
                {editingId === agent.id ? (
                  <Select value={editVoiceId} onValueChange={setEditVoiceId}>
                    <SelectTrigger className="h-8 w-36">
                      <SelectValue placeholder="Select voice..." />
                    </SelectTrigger>
                    <SelectContent>
                      {matchingPresets.length > 0 && (
                        <>
                          <SelectItem value="" disabled className="text-xs text-muted-foreground">
                            Recommended for {agent.gender}/{agent.accent}
                          </SelectItem>
                          {matchingPresets.map(preset => (
                            <SelectItem key={preset.id} value={preset.id}>
                              {preset.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="" disabled className="text-xs text-muted-foreground border-t mt-1 pt-1">
                            All voices
                          </SelectItem>
                        </>
                      )}
                      {VOICE_PRESETS.filter(p => !matchingPresets.includes(p)).map(preset => (
                        <SelectItem key={preset.id} value={preset.id}>
                          {preset.name} ({preset.gender}, {preset.accent})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  currentPreset ? (
                    <Badge variant="outline" className="font-normal">
                      {currentPreset.name}
                    </Badge>
                  ) : agent.elevenlabs_voice_id ? (
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {agent.elevenlabs_voice_id.substring(0, 12)}...
                    </code>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Not set</span>
                  )
                )}
              </TableCell>
              <TableCell className="text-right">
                {editingId === agent.id ? (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelEdit}
                      disabled={updateAgentMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveEdit}
                      disabled={updateAgentMutation.isPending}
                    >
                      {updateAgentMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(agent)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        {agents.length === 0 && (
          <TableRow>
            <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
              No voice agents in this category
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mic className="h-5 w-5 text-primary" />
                ElevenLabs Voice Agents
              </CardTitle>
              <CardDescription>
                Manage voice agents used in simulations. Edit name, description, and agent ID inline.
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Voice
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="patient" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="patient" className="flex items-center gap-2">
                <Stethoscope className="h-4 w-4" />
                Patient ({patientAgents.length})
              </TabsTrigger>
              <TabsTrigger value="customer" className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Customer ({customerAgents.length})
              </TabsTrigger>
              <TabsTrigger value="examiner" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Examiner ({examinerAgents.length})
              </TabsTrigger>
              <TabsTrigger value="podcast" className="flex items-center gap-2">
                <Podcast className="h-4 w-4" />
                Podcast ({podcastAgents.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="patient" className="mt-4">
              {renderAgentTable(patientAgents)}
            </TabsContent>

            <TabsContent value="customer" className="mt-4">
              {renderAgentTable(customerAgents)}
            </TabsContent>

            <TabsContent value="examiner" className="mt-4">
              {renderAgentTable(examinerAgents)}
            </TabsContent>

            <TabsContent value="podcast" className="mt-4">
              {renderAgentTable(podcastAgents)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Voice Agent</DialogTitle>
            <DialogDescription>
              Add a new ElevenLabs voice agent to the pool
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newAgent.name}
                onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                placeholder="e.g., Australian Female 3"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newAgent.description}
                onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                placeholder="e.g., Mid-30s professional, calm tone, moderate pace"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-id">ElevenLabs Agent ID</Label>
              <Input
                id="agent-id"
                value={newAgent.elevenlabs_agent_id}
                onChange={(e) => setNewAgent({ ...newAgent, elevenlabs_agent_id: e.target.value })}
                placeholder="agent_..."
                className="font-mono"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Agent Type</Label>
                <Select
                  value={newAgent.agent_type}
                  onValueChange={(value: 'patient' | 'customer' | 'examiner' | 'podcast') => 
                    setNewAgent({ ...newAgent, agent_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="examiner">Examiner</SelectItem>
                    <SelectItem value="podcast">Podcast</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={newAgent.gender}
                  onValueChange={(value: 'male' | 'female') => 
                    setNewAgent({ ...newAgent, gender: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Accent</Label>
                <Select
                  value={newAgent.accent}
                  onValueChange={(value: 'AU' | 'US' | 'UK') => 
                    setNewAgent({ ...newAgent, accent: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AU">Australian</SelectItem>
                    <SelectItem value="US">American</SelectItem>
                    <SelectItem value="UK">British</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAgent} 
              disabled={!newAgent.name.trim() || !newAgent.elevenlabs_agent_id.trim() || createAgentMutation.isPending}
            >
              {createAgentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Voice Agent'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
